<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePhonebookStore } from '@/stores/phonebook.store'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import i18n from '@/plugins/i18n'

import PhoneBookSearch from '@/components/phonebook/PhoneBookSearch.vue'
import PhoneBookList from '@/components/phonebook/PhoneBookList.vue'
import PhoneBookFormDialog from '@/components/phonebook/PhoneBookFormDialog.vue'

const t = i18n.global.t
const route = useRoute()
const router = useRouter()
const phonebookStore = usePhonebookStore()
const { notifyError, notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()

const contacts = ref([])
const loading = ref(false)
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const editItemId = ref(null)

const currentFilter = ref('')

const fetchContacts = async(force = false) => {
  if (phonebookStore.allContacts.length > 0 && !force) {
    applyFilter()
    return
  }

  loading.value = true
  try {
    await phonebookStore.fetchContacts()
    applyFilter()
  } catch (error) {
    notifyError(t('phonebook.fetchError'))
  } finally {
    loading.value = false
  }
}

const applyFilter = () => {
  const needle = currentFilter.value.toLowerCase()

  if (needle === '') {
    contacts.value = phonebookStore.allContacts
    return
  }

  contacts.value = phonebookStore.allContacts.filter(contact => {
    const name = (contact.name || '').toLowerCase()
    const phone = (contact.phone_number || '').toLowerCase()

    return name.includes(needle) || phone.includes(needle)
  })
}

const handleFilter = (value, updateInputText) => {
  if (updateInputText) {
    updateInputText(value)
  }

  currentFilter.value = value

  applyFilter()
}

const handleDelete = async(id) => {
  try {
    await phonebookStore.deleteContact(id)
    notifySuccess(t('phonebook.notifications.deleteSuccess'))
    await fetchContacts(true)
  } catch (error) {
    notifyError(t('phonebook.notifications.deleteError'))
  }
}

const openCreateDialog = () => router.push({ query: { ...route.query, create: '1' } })
const openEditDialog = (id) => {
  router.push({ query: { ...route.query, edit: id } })
}
const syncUrlOnClose = () => {
  const query = { ...route.query }
  delete query.create
  delete query.edit
  router.push({ query })
}

const onSaveSuccess = async() => {
  syncUrlOnClose()
  await fetchContacts(true)
}

watch(
  () => route.query,
  (query) => {
    showCreateDialog.value = !!query.create
    showEditDialog.value = !!query.edit
    editItemId.value = query.edit || null
  },
  { immediate: true }
)

watch([showCreateDialog, showEditDialog], ([newCreate, newEdit]) => {
  if (!newCreate && !newEdit && (route.query.create || route.query.edit)) {
    syncUrlOnClose()
  }
})

onMounted(() => {
  fetchContacts(true)
})
</script>

<template>
  <q-page
    class="q-pa-md"
  >
    <div class="q-mb-md">
      <h1 class="text-h5 text-weight-bold q-ma-none">
        {{ $t('phonebook.title') }}
      </h1>
      <p class="text-subtitle2 text-grey-7">
        {{ $t('phonebook.subtitle') }}
      </p>
    </div>

    <div class="q-mb-md">
      <PhoneBookSearch @filter="handleFilter" />
    </div>

    <div class="">
      <PhoneBookList
        :contacts="contacts"
        :loading="loading"
        @edit="openEditDialog"
        @delete="handleDelete"
      />
    </div>

    <q-fab
      color="primary"
      glossy
      icon="mdi-plus"
      direction="up"
      class="fab-create"
      @click="openCreateDialog"
    />

    <PhoneBookFormDialog
      v-model="showCreateDialog"
      mode="create"
      :full-screen="isMobile"
      title="phonebook.create.title"
      @saved="onSaveSuccess"
    />

    <PhoneBookFormDialog
      v-model="showEditDialog"
      mode="edit"
      :item-id="editItemId"
      :full-screen="isMobile"
      title="phonebook.edit.title"
      @saved="onSaveSuccess"
    />
  </q-page>
</template>

<style scoped>
.fab-create {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 1;
}

@media (max-width: 768px) {
  .fab-create {
    bottom: 5rem;
  }
}

/* Il tasto "+" passava inosservato, soprattutto da desktop dove la pagina e' larga e vuota.
   Pulsazione lenta + due onde concentriche tipo sasso nell'acqua: si notano senza infastidire.
   Le onde partono dal bordo del bottone e svaniscono espandendosi, sfalsate di 1,2 s.
   Tutto si ferma al passaggio del mouse e rispetta "riduci animazioni". */
.fab-create {
  animation: fab-pulse 2.6s ease-in-out infinite;
}

.fab-create::before,
.fab-create::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: 50%;
  border: 2px solid currentColor;
  color: var(--q-primary);
  opacity: 0;
  animation: fab-ripple 2.4s ease-out infinite;
  pointer-events: none;
}

.fab-create::after {
  animation-delay: 1.2s;
}

.fab-create:hover,
.fab-create:hover::before,
.fab-create:hover::after {
  animation-play-state: paused;
}

@keyframes fab-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2); }
  50%      { transform: scale(1.06); box-shadow: 0 8px 22px rgba(0, 0, 0, 0.3); }
}

@keyframes fab-ripple {
  0%   { transform: scale(1);   opacity: 0.55; }
  70%  { opacity: 0.12; }
  100% { transform: scale(2.1); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .fab-create,
  .fab-create::before,
  .fab-create::after { animation: none; }
}
</style>
